import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { createScheduledVisit, type MyFacility, type ScheduledVisit } from '../lib/api';
import './ScheduledVisitModal.css';

function localDateValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function ScheduledVisitModal({ facility, onClose, onScheduled }: { facility: MyFacility; onClose: () => void; onScheduled: (visit: ScheduledVisit) => void }) {
  const { getAccessTokenSilently } = useAuth0();
  const today = new Date();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const [visibleMonth, setVisibleMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(localDateValue(tomorrow));
  const [time, setTime] = useState('10:00');
  const [selectedPetIds, setSelectedPetIds] = useState<string[]>(facility.pets.map((pet) => pet.id));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const monthTitle = visibleMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const firstWeekday = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1).getDay();
  const daysInMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0).getDate();
  const calendarCells = [...Array.from({ length: firstWeekday }, () => null), ...Array.from({ length: daysInMonth }, (_, index) => index + 1)];
  const minimumDate = localDateValue(today);

  const togglePet = (petId: string) => {
    setSelectedPetIds((current) => current.includes(petId) ? current.filter((id) => id !== petId) : [...current, petId]);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedDate || !selectedPetIds.length) {
      setError('Choose a visit date and at least one pet.');
      return;
    }
    const startsAt = new Date(`${selectedDate}T${time}:00`);
    if (Number.isNaN(startsAt.getTime()) || startsAt <= new Date()) {
      setError('Choose a future date and time.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const visit = await createScheduledVisit(token, { facility_id: facility.id, pet_ids: selectedPetIds, starts_at: startsAt.toISOString(), notes: notes.trim() || undefined });
      onScheduled(visit);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to schedule this visit.');
    } finally {
      setSaving(false);
    }
  };

  const changeMonth = (delta: number) => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));

  return <div className="scheduled-visit-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="scheduled-visit-dialog" role="dialog" aria-modal="true" aria-labelledby="scheduled-visit-title">
      <header className="scheduled-visit-header"><div><p className="eyebrow">My Facilities</p><h2 id="scheduled-visit-title">Schedule a visit</h2><span>{facility.name}</span></div><button type="button" className="icon-button" onClick={onClose} aria-label="Close schedule visit"><X size={19} /></button></header>
      <form onSubmit={submit}>
        <div className="scheduled-visit-grid">
          <section className="visit-calendar" aria-label="Choose visit date">
            <div className="visit-calendar-heading"><button type="button" className="icon-button" onClick={() => changeMonth(-1)} aria-label="Previous month" disabled={visibleMonth.getFullYear() === today.getFullYear() && visibleMonth.getMonth() <= today.getMonth()}><ChevronLeft size={18} /></button><h3>{monthTitle}</h3><button type="button" className="icon-button" onClick={() => changeMonth(1)} aria-label="Next month"><ChevronRight size={18} /></button></div>
            <div className="visit-calendar-grid visit-calendar-weekdays">{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((weekday, index) => <span key={`${weekday}-${index}`}>{weekday}</span>)}</div>
            <div className="visit-calendar-grid">{calendarCells.map((day, index) => {
              if (!day) return <span className="visit-calendar-empty" key={`empty-${index}`} />;
              const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), day);
              const value = localDateValue(date);
              return <button type="button" key={value} className={selectedDate === value ? 'selected' : ''} disabled={value < minimumDate} aria-pressed={selectedDate === value} onClick={() => setSelectedDate(value)}>{day}</button>;
            })}</div>
            <label className="visit-time-field">Visit time<input type="time" value={time} onChange={(event) => setTime(event.target.value)} required /></label>
          </section>
          <section className="visit-pet-picker"><h3>Pets attending</h3>{facility.pets.length ? facility.pets.map((pet) => <label key={pet.id}><input type="checkbox" checked={selectedPetIds.includes(pet.id)} onChange={() => togglePet(pet.id)} /><span>{pet.name}</span></label>) : <p>No linked pets found for this facility.</p>}<label className="visit-notes-field">Notes (optional)<textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={1000} rows={4} placeholder="Reason for visit or details to remember" /></label></section>
        </div>
        {error && <p className="form-error visit-error" role="alert">{error}</p>}
        <footer className="scheduled-visit-actions"><span><CalendarDays size={15} /> A reminder will appear 2 days before the visit.</span><div><button type="button" className="button button-secondary" onClick={onClose}>Cancel</button><button type="submit" className="button button-primary" disabled={saving || !facility.pets.length}>{saving ? 'Scheduling...' : 'Schedule visit'}</button></div></footer>
      </form>
    </section>
  </div>;
}
